import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, FileText, ClipboardList, PlayCircle, ShieldCheck, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { TablePagination } from '@/components/ui';
import { useWorkOrders } from './hooks/useWorkOrders';
import { WorkOrdersTable } from './components/WorkOrdersTable';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { KpiGrid, type KpiItem } from '@/components/ui/KpiGrid';
import { DataFilterToolbar, type FilterDefinition } from '@/components/ui/DataFilterToolbar';

export default function WorkOrdersPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { workOrders, kpis, isLoading, deleteWorkOrder } = useWorkOrders(projectId);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'card' | 'compact'>('table');
  const pageSize = 10;

  const filteredItems = useMemo(() => {
    let result = activeTab === 'all' ? workOrders : workOrders.filter(item => item.type === activeTab);
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((item) => [item.title, item.code, item.projectName, item.zoneName, item.leadName].some((value) => String(value || '').toLowerCase().includes(query)));
    }
    return result;
  }, [workOrders, activeTab, searchQuery]);

  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, currentPage]);

  const percent = (val: number) => kpis.total > 0 ? (val / kpis.total * 100).toFixed(1) : '0.0';

  const kpiItems: KpiItem[] = [
    { title: 'Tổng công việc', value: kpis.total, suffix: '', icon: ClipboardList, color: 'text-blue-600', bg: 'bg-blue-50', trend: 'neutral', trendValue: 'Tổng quan', chartColor: 'bg-blue-500', chartData: [5, 5, 5, 5, 5] },
    { title: 'Hoàn thành', value: kpis.completed, suffix: '', icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', trend: 'up', trendValue: `${percent(kpis.completed)}% tổng`, chartColor: 'bg-emerald-500', chartData: [1, 2, 3, 4, 5] },
    { title: 'Đang thực hiện', value: kpis.inProgress, suffix: '', icon: PlayCircle, color: 'text-blue-500', bg: 'bg-blue-50', trend: 'up', trendValue: `${percent(kpis.inProgress)}% tổng`, chartColor: 'bg-blue-500', chartData: [2, 3, 2, 4, 3] },
    { title: 'Chưa bắt đầu', value: kpis.notStarted, suffix: '', icon: ShieldCheck, color: 'text-amber-500', bg: 'bg-amber-50', trend: 'neutral', trendValue: `${percent(kpis.notStarted)}% tổng`, chartColor: 'bg-amber-500', chartData: [1, 1, 1, 1, 1] },
    { title: 'Tạm dừng', value: kpis.paused, suffix: '', icon: XCircle, color: 'text-indigo-500', bg: 'bg-indigo-50', trend: 'neutral', trendValue: `${percent(kpis.paused)}% tổng`, chartColor: 'bg-indigo-500', chartData: [0, 0, 1, 0, 0] },
    { title: 'Quá hạn', value: kpis.overdue, suffix: '', icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50', trend: 'down', trendValue: `${percent(kpis.overdue)}% tổng`, chartColor: 'bg-rose-500', chartData: [0, 1, 0, 1, 0] },
  ];

  const filterDefs: FilterDefinition[] = [
    {
      id: 'tab',
      label: 'Danh mục',
      options: [
        { value: 'all', label: 'Tất cả' },
        { value: 'construction', label: 'Thi công' },
        { value: 'maintenance', label: 'Bảo dưỡng' }
      ]
    },
    { id: 'project', label: 'Dự án', options: [] },
    { id: 'type', label: 'Loại công việc', options: [] },
    { id: 'status', label: 'Trạng thái', options: [] },
    { id: 'assignee', label: 'Người phụ trách', options: [] }
  ];

  return (
    <StandardListLayout
      title="Tất cả công việc"
      description="Quản lý và theo dõi tất cả công việc trên các dự án"
      isLoading={isLoading}
      viewMode={viewMode}
      kpiSection={<KpiGrid kpis={kpiItems} />}
      toolbarSection={
        <DataFilterToolbar
          search={searchQuery}
          onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1); }}
          searchPlaceholder="Tìm kiếm công việc..."
          filterDefs={filterDefs}
          filterValues={{ tab: activeTab }}
          onFilterChange={(k, v) => {
            if (k === 'tab') { setActiveTab(v || 'all'); setCurrentPage(1); }
          }}
          actions={
            <>
              <button className="h-9 px-3 hidden sm:flex items-center gap-2 rounded-md border border-slate-200 bg-white sm:text-sm text-xs text-slate-600 hover:bg-slate-50 transition-colors shrink-0 shadow-sm">
                <FileText className="w-4 h-4 text-emerald-600" />
                Xuất Excel
              </button>
              <button
                onClick={() => navigate('/work-orders/new')}
                className="h-9 px-3 flex items-center gap-2 rounded-md bg-emerald-600 sm:text-sm text-xs text-white hover:bg-emerald-700 transition-all border-none shrink-0 shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Thêm công việc
              </button>
            </>
          }
        />
      }
      tableSection={
        <div className="flex flex-col h-full bg-white">
          <WorkOrdersTable items={pagedItems} onDelete={deleteWorkOrder} />
          <div className="p-3 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4 mt-auto shrink-0 bg-white">
            <div className="sm:text-sm text-xs text-slate-500">
              Hiển thị {pagedItems.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} - {Math.min(currentPage * pageSize, filteredItems.length)} trong tổng số {filteredItems.length} công việc
            </div>
            <div className="flex items-center gap-4">
              <TablePagination
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                totalItems={filteredItems.length}
                pageSize={pageSize}
                className="border-none p-0"
              />
            </div>
          </div>
        </div>
      }
      cardSection={
        <div className="bg-slate-50 border border-slate-200 border-dashed rounded-lg flex flex-col items-center justify-center p-8 text-center text-slate-500 text-sm h-full mx-4 mb-4">
          Giao diện thẻ (Card) chưa được hỗ trợ. Vui lòng xem trên Desktop.
        </div>
      }
    />
  );
}
